import React from "react";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import {
  FaRegHeart,
  FaHandHoldingHeart,
  FaCheckCircle,
  FaBookOpen,
  FaRupeeSign,
  FaHandshake,
  FaUsers,
  FaLeaf,
} from "react-icons/fa";
import "./SustainableLivelihoods.css";

const FOCUS_AREAS = [
  {
    img: "assets/images/sections/skill.jpeg",
    icon: FaBookOpen,
    title: "Skills Development",
    text: "We conduct hands-on training for both farm and non-farm based livelihoods.",
  },
  {
    img: "assets/images/sections/fi.webp",
    icon: FaRupeeSign,
    title: "Financial Empowerment",
    text: "We help form rural finance groups and arrange credit to encourage local enterprise.",
  },
  {
    img: "assets/images/sections/mr.webp",
    icon: FaHandshake,
    title: "Market Integration",
    text: "We create vital linkages to ensure participants can get the best price for their products.",
  },
  {
    img: "assets/images/sections/di.webp",
    icon: FaUsers,
    title: "Diversified Income",
    text: "Our advocacy supports diverse income streams to build community resilience.",
  },
];

const SustainableLivelihoods = () => {
  return (
    <>
      <Header />

      {/* ================= HERO ================= */}
      <section className="sl-page">
        <div className="sl-container">
          <motion.div
            className="sl-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="sl-hero__image"
              style={{ backgroundImage: "url(/assets/images/backgrounds/banana_banner_1521x482.png)" }}
            />
            <div className="sl-hero__overlay" />
            <div className="sl-hero__dots" />
            <svg className="sl-hero__squiggle" viewBox="0 0 220 40" fill="none">
              <path
                d="M0 20 C 40 0, 60 40, 100 20 S 180 0, 220 20"
                stroke="rgba(255,255,255,0.25)"
                strokeWidth="1.5"
              />
            </svg>

            <div className="sl-hero__content">
              <div className="sl-hero__mark">
                <FaRegHeart className="sl-hero__mark-heart" />
                <FaHandHoldingHeart className="sl-hero__mark-hand" />
              </div>

              <span className="sl-eyebrow sl-eyebrow--light">Our Commitment</span>

              <motion.h2
                className="sl-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Sustainable Livelihoods
              </motion.h2>

              <motion.p
                className="sl-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Vikash supports sustainable livelihoods through healthcare,
                education, and skill development, fostering resilience,
                inclusion, and growth in marginalized communities.
              </motion.p>

              <motion.div
                className="sl-hero__pill"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.45 }}
              >
                <FaCheckCircle />
                Building resilient communities for a better tomorrow
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= APPROACH ================= */}
      <section className="sl-approach">
        <div className="sl-container sl-approach__grid">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="sl-eyebrow">Our Approach</span>
            <h2 className="sl-heading">
              To Create Opportunities
              <br />
              for Dignified Living
            </h2>
            <span className="sl-heading__underline" />

            <p className="sl-approach__lead">
              Our approach to creating economic independence.
            </p>
            <p className="sl-approach__text">
              Vikash empowers indigenous tribes, women, persons-with-disabilities,
              and other disadvantaged communities by building sustainable paths
              to economic independence.
            </p>
            <p className="sl-approach__text">
              We focus on a holistic approach that combines skills training,
              financial access, and market integration to create lasting change
              and enhance income levels.
            </p>
          </motion.div>

          <motion.div
            className="sl-approach__imageWrap"
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="sl-approach__dots" />
            <span className="sl-approach__circle" />
            <img
              src="assets/images/sections/ssl1.jpg"
              alt="Sustainable Livelihoods"
              className="sl-approach__image"
            />
          </motion.div>
        </div>
      </section>

      {/* ================= FOCUS AREAS ================= */}
      <section className="sl-focus">
        <div className="sl-container">
          <motion.div
            className="sl-focus__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="sl-eyebrow sl-eyebrow--center">Our Key Focus Areas</span>
            <h2 className="sl-heading sl-heading--center">
              Empowering Communities. Enriching Lives.
            </h2>
            <div className="sl-focus__divider">
              <span />
              <FaLeaf />
              <span />
            </div>
          </motion.div>

          <div className="sl-focus__grid">
            {FOCUS_AREAS.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  className="sl-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.1 * i }}
                >
                  <div className="sl-card__imageWrap">
                    <img src={f.img} alt={f.title} className="sl-card__image" />
                    <div className="sl-card__icon">
                      <Icon />
                    </div>
                  </div>
                  <div className="sl-card__body">
                    <h5 className="sl-card__title">{f.title}</h5>
                    <p className="sl-card__text">{f.text}</p>
                    <span className="sl-card__underline" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll to top button */}
      <a href="#" data-target="html" className="scroll-to-target sl-scroll-top">
        <i className="bi bi-arrow-up fs-4"></i>
      </a>
    </>
  );
};

export default SustainableLivelihoods;